"use client"

import React, { type ReactNode, useEffect, useRef, useState, useMemo } from "react"
import { GuideNav, type NavigationStep } from "./GuideNav"
import { PageWelcome } from "./PageWelcome"
import { useLocation } from "react-router-dom"
import { checkAuthStatus } from '../../utils/authUtils';
import { SectionProvider } from '../../contexts/SectionContext';
import { cn } from "../../utils/cn"

interface GuideProps {
  children: ReactNode
  homePageContent?: ReactNode // Optional: Custom content for "Home Page" state
  navigation?: ReactNode // Optional: Custom navigation component
  steps?: NavigationStep[] // Navigation steps for prev/next calculation
  disableContentPadding?: boolean // Optional: Disable default content padding (pb-6)
  enableTOC?: boolean // Optional: Enable automatic table of contents (desktop only)
}

const LOCKED_PAGES = [
  "/dropshipping/integration",
  "/dropshipping/operations",
  "/dropshipping/prepare",
  "/dropshipping/shipping",
  "/dropshipping/support",
  "/dropshipping/warehouse-info",
]

export function Guide({ children, homePageContent, navigation, steps, disableContentPadding = false, enableTOC = false }: GuideProps) {
  const location = useLocation()
  const pathname = location.pathname
  const isHomePage = pathname === "/" ||
    pathname === "/dropshipping" ||
    pathname === "/shipping" ||
    pathname === "/admin" ||
    pathname === "/account"  // Only /account root, not subpages
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)

  const mobileScrollRef = useRef<HTMLDivElement>(null)

  // We no longer own the scroll container, we find it from the context/DOM
  const [desktopScrollContainer, setDesktopScrollContainer] = useState<HTMLElement | null>(null)

  // Ref to the content wrapper to find the parent scroll container
  const contentWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Find the closest main scroll container (from Main.tsx)
    if (contentWrapperRef.current) {
      // The structure in Main.tsx is: <main className="... overflow-y-auto ...">
      // We search upwards for that specific main tag.
      const parentMain = contentWrapperRef.current.closest('main');
      if (parentMain) {
        console.log('[Guide] Found parent scroll container:', parentMain);
        setDesktopScrollContainer(parentMain as HTMLElement);
      } else {
        console.warn('[Guide] Could not find parent scroll container');
      }
    }
  }, []);


  useEffect(() => {
    const checkAuth = async () => {
      const isAuth = await checkAuthStatus()
      setIsAuthenticated(isAuth)
    }
    checkAuth()

    const handleAuthChange = (e: Event) => {
      const customEvent = e as CustomEvent
      if (customEvent.detail && customEvent.detail.isAuthenticated !== undefined) {
        setIsAuthenticated(customEvent.detail.isAuthenticated)
      } else {
        checkAuth()
      }
    }

    window.addEventListener('authStateChange', handleAuthChange)
    return () => {
      window.removeEventListener('authStateChange', handleAuthChange)
    }
  }, [])

  useEffect(() => {
    if (mobileScrollRef.current) {
      mobileScrollRef.current.scrollTo({
        top: 0,
        behavior: "auto",
      })
    }
    // For desktop, we trigger scroll on the found container
    if (desktopScrollContainer) {
      desktopScrollContainer.scrollTo({
        top: 0,
        behavior: "auto",
      })
    }
  }, [pathname, desktopScrollContainer])

  // Default navigation to steps-based GuideNav if steps provided but nav is missing
  const NavComponent = navigation || (steps ? <GuideNav variant="sidebar" steps={steps} /> : <GuideNav variant="sidebar" />)

  return (
    <>
      {/* Mobile view */}
      <div className="lg:hidden h-full flex flex-col overflow-hidden">
        <div
          ref={mobileScrollRef}
          data-scroll-container="mobile"
          className={cn(
            "flex-1 flex flex-col overflow-x-hidden scrollbar-hide",
            LOCKED_PAGES.includes(pathname) ? "overflow-hidden bg-bgb" : "overflow-y-auto"
          )}
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch'
          } as React.CSSProperties}
        >
          {isHomePage ? (
            <>
              {/* Homepage content */}
              {homePageContent || null}
              {/* 
                Smart navigation rendering for mobile:
                - If homePageContent is Welcome component, skip (Welcome handles navigation internally)
                - Otherwise, automatically render GuideNav overview for mobile
              */}
              {navigation && React.isValidElement(navigation) && navigation.type === GuideNav &&
                React.isValidElement(homePageContent) && homePageContent.type !== PageWelcome
                ? React.cloneElement(navigation as React.ReactElement<any>, { variant: 'overview' })
                : null}
            </>
          ) : (
            children
          )}
        </div>
      </div>

      {/* Desktop view - Refactored for Full Page Scroll */}
      {/* We add 'py-6' here to ensure vertical spacing for the whole layout context if needed, 
          but usually the content wrapper handles it. Actually, per request 'py-6' on outer is good. */}
      {/* Note: The 'hidden lg:block' container itself doesn't restrict height anymore */}
      <div className="hidden lg:block px-6">
        {/*
          Refactored Layout:
          We define the layout structure once and conditionally wrap it with SectionProvider if enableTOC is true.
        */}
        {(() => {
          const LayoutStructure = (
            <div className="flex gap-6 relative" ref={contentWrapperRef}>
              {/* Left sidebar navigation - Sticky */}
              <aside className="sticky top-6 z-10 w-72 flex-shrink-0 self-start h-[calc(100vh-3rem)] flex flex-col mt-6">
                <div className="h-full bg-w backdrop-blur-sm rounded-2xl shadow-sm overflow-hidden flex flex-col">
                  {/* Internal scroll for sidebar content */}
                  <div className="flex-1 px-5 py-3 overflow-y-auto scrollbar-hide">
                    {NavComponent}
                  </div>
                </div>
              </aside>

              {/* Main content area */}
              <div
                data-scroll-container="desktop-content"
                className={`flex-1 min-w-0 flex flex-col min-h-screen ${disableContentPadding ? '' : ''}`}
              >
                {isHomePage ? (
                  homePageContent || children
                ) : (
                  children
                )}
              </div>
            </div>
          )

          if (enableTOC) {
            return (
              <SectionProvider scrollContainer={desktopScrollContainer}>
                {LayoutStructure}
              </SectionProvider>
            )
          }

          return LayoutStructure
        })()}
      </div>
    </>
  )
}
